import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useEffect, useMemo } from "react";
import { Global, useTheme } from "@emotion/react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns/format";
import { isBefore } from "date-fns/isBefore";
import { isAfter } from "date-fns/isAfter";
import Close from "@engineering/icons/close";
import { CustomMonths, CustomMonthsContext } from "./CustomMonths";
import { GlobalStyles, DayPickerWrapper, DayPickerHeader, DayPickerFooter, Arrow, StyledButton } from "./DatePicker.styles";
import { IconButton } from "../../../../../Ui";
var DatePicker = function(_param) {
    var from = _param.from, to = _param.to, dayCountText = _param.dayCountText, _scrollToSelectedMonth = _param.scrollToSelectedMonth, scrollToSelectedMonth = _scrollToSelectedMonth === void 0 ? false : _scrollToSelectedMonth, onDatesChange = _param.onDatesChange, onClose = _param.onClose, locale = _param.locale, dateFormat = _param.dateFormat, size = _param.size, onScrollToBottom = _param.onScrollToBottom, vertical = _param.vertical, props = _object_without_properties(_param, [
        "from",
        "to",
        "dayCountText",
        "scrollToSelectedMonth",
        "onDatesChange",
        "onClose",
        "locale",
        "dateFormat",
        "size",
        "onScrollToBottom",
        "vertical"
    ]);
    var theme = useTheme();
    useEffect(function() {
        var ref, ref1, ref2, ref3;
        if (!scrollToSelectedMonth) {
            return;
        }
        var selectedDay = document.querySelector(".rdp-day_selected");
        var selectedMonthElm = selectedDay === null || selectedDay === void 0 ? void 0 : (ref = selectedDay.parentElement) === null || ref === void 0 ? void 0 : (ref1 = ref.parentElement) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.parentElement) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.parentElement) === null || ref3 === void 0 ? void 0 : ref3.parentElement;
        if (selectedMonthElm) {
            selectedMonthElm.scrollIntoView();
        }
    }, [
        scrollToSelectedMonth
    ]);
    var ref = useState(from || null), selectedFrom = ref[0], setFrom = ref[1];
    var ref1 = useState(to || null), selectedTo = ref1[0], setTo = ref1[1];
    var ref2 = useState(undefined), hoveredTo = ref2[0], setHoveredTo = ref2[1];
    var isSelectingFirstDay = function(date) {
        var isBeforeFirstDay = selectedFrom && isBefore(date, selectedFrom);
        var isRangeSelected = selectedFrom && selectedTo;
        return !selectedFrom || isBeforeFirstDay || isRangeSelected;
    };
    var handleOnDayClick = function(date, modifiers) {
        if (modifiers.disabled) {
            return;
        }
        if (isSelectingFirstDay(date)) {
            setFrom(date);
            setTo(null);
            setHoveredTo(undefined);
        } else {
            setTo(date);
            setHoveredTo(date);
            if (typeof onDatesChange !== "undefined") {
                onDatesChange({
                    from: selectedFrom,
                    to: date
                });
            }
        }
    };
    var getHighlightedDays = function() {
        var highlightRules = [
            function(day) {
                if (selectedFrom && selectedTo) {
                    return isAfter(day, selectedFrom) && isBefore(day, selectedTo);
                }
                return false;
            }, 
        ];
        if (selectedFrom) {
            highlightRules.push(selectedFrom);
        }
        if (selectedFrom && hoveredTo) {
            highlightRules.push({
                from: selectedFrom,
                to: hoveredTo
            });
        }
        if (selectedFrom && selectedTo) {
            highlightRules.push({
                from: selectedFrom,
                to: selectedTo
            });
        }
        return highlightRules;
    };
    var getModifiers = function() {
        var modifiers = {};
        if (selectedFrom) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                from: [
                    selectedFrom
                ]
            });
        }
        if (hoveredTo) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                to: hoveredTo
            });
        } else if (selectedTo) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                to: selectedTo
            });
        }
        return modifiers;
    };
    var handleDayMouseEnter = function(day) {
        if (from && isBefore(day, from) || isSelectingFirstDay(day)) {
            return;
        }
        setHoveredTo(day);
    };
    var context = useMemo(function() {
        return {
            onScrollToBottom: onScrollToBottom,
            vertical: vertical
        };
    }, [
        onScrollToBottom,
        vertical
    ]);
    return /*#__PURE__*/ _jsxs(CustomMonthsContext.Provider, {
        value: context,
        children: [
            /*#__PURE__*/ _jsx(Global, {
                styles: [
                    GlobalStyles({
                        theme: theme
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(DayPickerWrapper, {
                children: [
                    /*#__PURE__*/ _jsxs(DayPickerHeader, {
                        children: [
                            from ? format(from, dateFormat, {
                                locale: locale
                            }) : "---",
                            /*#__PURE__*/ _jsx(Arrow, {}),
                            to ? format(to, dateFormat, {
                                locale: locale
                            }) : "---",
                            /*#__PURE__*/ _jsx(IconButton, {
                                icon: Close,
                                title: "close" // @TODO: Add translation
                                ,
                                onClick: onClose
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DayPicker, _object_spread({
                        components: {
                            Months: CustomMonths
                        },
                        onDayClick: handleOnDayClick,
                        onDayMouseEnter: handleDayMouseEnter,
                        selected: getHighlightedDays(),
                        modifiers: getModifiers(),
                        disabled: {
                            before: new Date()
                        },
                        locale: locale,
                        modifiersClassNames: {
                            outside: "rdp-day_outside",
                            disabled: "rdp-day_disabled",
                            today: "rdp-day_today",
                            from: "rdp-day_range_start",
                            to: "rdp-day_range_end"
                        }
                    }, props)),
                    /*#__PURE__*/ _jsx(DayPickerFooter, {
                        children: /*#__PURE__*/ _jsx(StyledButton, {
                            size: size,
                            fullWidth: true,
                            onClick: onClose,
                            children: dayCountText
                        })
                    })
                ]
            })
        ]
    });
};
export { DatePicker };
